import type { Tenant } from '~/api/tenant';

/** Uma cor em canais de 0 a 255. */
export interface Rgb {
  red: number;
  green: number;
  blue: number;
}

export type TenantColors = Pick<Tenant, 'cor_primaria' | 'cor_secundaria'>;

/** A mesma regra do backend (`#rrggbb`), sem exigir minúsculas: a saída é sempre normalizada. */
const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;
const HEX_RADIX = 16;
const CHANNEL_MAX = 255;

const WHITE: Rgb = { red: CHANNEL_MAX, green: CHANNEL_MAX, blue: CHANNEL_MAX };
const BLACK: Rgb = { red: 0, green: 0, blue: 0 };

export const LIGHT_INK: Rgb = WHITE;
export const DARK_INK: Rgb = { red: 10, green: 20, blue: 16 };

const SHADE_RATIO = 0.12;
const TINT_RATIO = 0.12;
const BRAND_TINT_WHITE_RATIO = 0.9;

/** Constantes da luminância relativa da WCAG 2.0 (sRGB linearizado). */
const LINEAR_THRESHOLD = 0.03928;
const LINEAR_DIVISOR = 12.92;
const GAMMA_OFFSET = 0.055;
const GAMMA_DIVISOR = 1.055;
const GAMMA_EXPONENT = 2.4;
const RED_WEIGHT = 0.2126;
const GREEN_WEIGHT = 0.7152;
const BLUE_WEIGHT = 0.0722;
const CONTRAST_FLARE = 0.05;

const THEME_COLOR_META_SELECTOR = 'meta[name="theme-color"]';
/** Guarda o `theme-color` do `index.html` para devolvê-lo quando o escritório perde as cores. */
const DEFAULT_CONTENT_ATTRIBUTE = 'data-default-content';

const THEME_PROPERTIES = [
  '--rx-brand',
  '--rx-brand-2',
  '--rx-brand-tint',
  '--rx-brand-ink',
  '--ion-color-primary',
  '--ion-color-primary-rgb',
  '--ion-color-primary-shade',
  '--ion-color-primary-tint',
  '--ion-color-primary-contrast',
  '--ion-color-primary-contrast-rgb',
] as const;

type ThemeProperty = (typeof THEME_PROPERTIES)[number];
type ThemePalette = Record<ThemeProperty, string>;

export function hexToRgb(hex: string): Rgb | null {
  if (!HEX_COLOR_PATTERN.test(hex)) {
    return null;
  }
  const value = Number.parseInt(hex.slice(1), HEX_RADIX);
  return {
    red: (value >> 16) & CHANNEL_MAX,
    green: (value >> 8) & CHANNEL_MAX,
    blue: value & CHANNEL_MAX,
  };
}

export function rgbToHex({ red, green, blue }: Rgb): string {
  const digits = [red, green, blue].map((channel) => channel.toString(HEX_RADIX).padStart(2, '0'));
  return `#${digits.join('')}`;
}

function mix(color: Rgb, target: Rgb, targetRatio: number): Rgb {
  const channel = (from: number, to: number): number =>
    Math.round(from + (to - from) * targetRatio);
  return {
    red: channel(color.red, target.red),
    green: channel(color.green, target.green),
    blue: channel(color.blue, target.blue),
  };
}

/** Mistura `whiteRatio` de branco na cor: 0 devolve a cor, 1 devolve branco. */
export function mixWithWhite(color: Rgb, whiteRatio: number): Rgb {
  return mix(color, WHITE, whiteRatio);
}

export function shade(color: Rgb, blackRatio: number = SHADE_RATIO): Rgb {
  return mix(color, BLACK, blackRatio);
}

export function tint(color: Rgb, whiteRatio: number = TINT_RATIO): Rgb {
  return mixWithWhite(color, whiteRatio);
}

function linearChannel(channel: number): number {
  const normalized = channel / CHANNEL_MAX;
  return normalized <= LINEAR_THRESHOLD
    ? normalized / LINEAR_DIVISOR
    : ((normalized + GAMMA_OFFSET) / GAMMA_DIVISOR) ** GAMMA_EXPONENT;
}

export function relativeLuminance({ red, green, blue }: Rgb): number {
  return (
    RED_WEIGHT * linearChannel(red) +
    GREEN_WEIGHT * linearChannel(green) +
    BLUE_WEIGHT * linearChannel(blue)
  );
}

function contrastRatio(first: Rgb, second: Rgb): number {
  const firstLuminance = relativeLuminance(first);
  const secondLuminance = relativeLuminance(second);
  const lighter = Math.max(firstLuminance, secondLuminance);
  const darker = Math.min(firstLuminance, secondLuminance);
  return (lighter + CONTRAST_FLARE) / (darker + CONTRAST_FLARE);
}

/** A tinta (texto e ícones sobre a marca) que mais contrasta com a cor; no empate, a clara. */
export function inkFor(background: Rgb): Rgb {
  return contrastRatio(background, LIGHT_INK) >= contrastRatio(background, DARK_INK)
    ? LIGHT_INK
    : DARK_INK;
}

function channelList({ red, green, blue }: Rgb): string {
  return `${red}, ${green}, ${blue}`;
}

function paletteFor({ cor_primaria, cor_secundaria }: TenantColors): ThemePalette | null {
  const primary = cor_primaria ? hexToRgb(cor_primaria) : null;
  if (!primary) {
    return null;
  }
  const secondary = cor_secundaria ? hexToRgb(cor_secundaria) : null;
  const ink = inkFor(primary);
  return {
    '--rx-brand': rgbToHex(primary),
    '--rx-brand-2': rgbToHex(secondary ?? shade(primary)),
    '--rx-brand-tint': rgbToHex(mixWithWhite(primary, BRAND_TINT_WHITE_RATIO)),
    '--rx-brand-ink': rgbToHex(ink),
    '--ion-color-primary': rgbToHex(primary),
    '--ion-color-primary-rgb': channelList(primary),
    '--ion-color-primary-shade': rgbToHex(shade(primary)),
    '--ion-color-primary-tint': rgbToHex(tint(primary)),
    '--ion-color-primary-contrast': rgbToHex(ink),
    '--ion-color-primary-contrast-rgb': channelList(ink),
  };
}

function themeColorMeta(): HTMLMetaElement | null {
  return document.querySelector<HTMLMetaElement>(THEME_COLOR_META_SELECTOR);
}

function setThemeColor(color: string): void {
  const meta = themeColorMeta();
  if (!meta) {
    return;
  }
  if (!meta.hasAttribute(DEFAULT_CONTENT_ATTRIBUTE)) {
    meta.setAttribute(DEFAULT_CONTENT_ATTRIBUTE, meta.content);
  }
  meta.content = color;
}

function restoreThemeColor(): void {
  const meta = themeColorMeta();
  const defaultContent = meta?.getAttribute(DEFAULT_CONTENT_ATTRIBUTE);
  if (!meta || defaultContent === null || defaultContent === undefined) {
    return;
  }
  meta.content = defaultContent;
  meta.removeAttribute(DEFAULT_CONTENT_ATTRIBUTE);
}

/**
 * Pinta o app com as cores do escritório: variáveis da marca e do `primary` do Ionic em `:root`
 * (o estilo inline vence o `:root` de `variables.css`) e o `theme-color` da barra do sistema.
 * Sem cor primária válida, os padrões de `variables.css` ficam; se um escritório anterior tinha
 * pintado o app, eles voltam.
 */
export function applyTenantTheme(colors: TenantColors): void {
  const rootStyle = document.documentElement.style;
  const palette = paletteFor(colors);
  if (!palette) {
    for (const property of THEME_PROPERTIES) {
      rootStyle.removeProperty(property);
    }
    restoreThemeColor();
    return;
  }
  for (const property of THEME_PROPERTIES) {
    rootStyle.setProperty(property, palette[property]);
  }
  setThemeColor(palette['--rx-brand']);
}
